import React, { useState } from 'react';
import CitationModal from './CitationModal';
import { Button } from 'js/design_system/Button';
import { TextButton } from 'js/design_system/TextButton';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';

export type PublishedLetterMetadata = {
  volume: number;
  issue: number;
  journalName: string;
  doi: string;
  publicationYear: number;
  url: string;
  authors: Array<{ name: string; family?: string; literal?: string }>;
};

export type Work = {
  id: string | number;
  title: string;
  authors?: Array<{ name: string }>;
  abstract?: string;
  doi?: string;
  url?: string;
  publication?: string;
  publicationYear?: number;
  publisher?: string;
};

type Props = {
  citations: { name: string; citation: string }[];
  entity_id: string | number;
  source: string;
  buttonClassName?: string;
  asTextButton?: boolean;
  work?: Work;
  publishedLetterMetadata?: PublishedLetterMetadata;
};

export const CitationButton: React.FC<Props> = ({
  citations,
  entity_id,
  source,
  buttonClassName,
  asTextButton = false,
  work,
  publishedLetterMetadata,
}) => {
  const [openModal, setOpenModal] = useState(false);

  const scheduleReminders = async () => {
    if (!entity_id) return;

    try {
      await fetchWithCsrf(routes.api_v0_cite_reminders_path(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ entity_id }),
      });
    } catch (error) {
      // Silently fail - this is a background feature
      console.error('Failed to schedule cite reminders:', error);
    }
  };

  const openCitationsModal = () => {
    setOpenModal(true);
    // Schedule reminder emails in the background
    scheduleReminders();
  };

  const closeCitationsModal = () => {
    setOpenModal(false);
  };

  const ButtonComponent = asTextButton ? TextButton : Button;
  const additionalButtonComponentProps = asTextButton
    ? { inline: true }
    : { secondary: true };

  return (
    <>
      <ButtonComponent
        onClick={openCitationsModal}
        icon="format_quote"
        className={buttonClassName}
        {...additionalButtonComponentProps}
      >
        Cite
      </ButtonComponent>
      <CitationModal
        open={openModal}
        citations={citations}
        onClose={closeCitationsModal}
        entity_id={entity_id}
        source={source}
        work={work}
        publishedLetterMetadata={publishedLetterMetadata}
      />
    </>
  );
};
